/* eslint-disable replo/no-tailwind-literal-color */
import type { ReactNode } from "react";
import type { ResizeHandle as ResizeHandleAxis } from "react-resizable";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { GripHorizontal, Minus, X } from "lucide-react";
import Draggable from "react-draggable";
import { Resizable } from "react-resizable";
import { z } from "zod";

// ---------------------------------------------------------------------------
// Resize handles
// ---------------------------------------------------------------------------

const RESIZE_HANDLES: ResizeHandleAxis[] = [
  "se",
  "sw",
  "ne",
  "nw",
  "s",
  "e",
  "w",
  "n",
];

const HANDLE_CURSOR: Record<ResizeHandleAxis, string> = {
  n: "cursor-ns-resize",
  s: "cursor-ns-resize",
  e: "cursor-ew-resize",
  w: "cursor-ew-resize",
  ne: "cursor-nesw-resize",
  sw: "cursor-nesw-resize",
  nw: "cursor-nwse-resize",
  se: "cursor-nwse-resize",
};

const HANDLE_POSITION: Record<ResizeHandleAxis, string> = {
  n: "left-0 top-0 h-1.5 w-full",
  s: "bottom-0 left-0 h-1.5 w-full",
  e: "right-0 top-0 h-full w-1.5",
  w: "left-0 top-0 h-full w-1.5",
  ne: "right-0 top-0 h-3 w-3",
  nw: "left-0 top-0 h-3 w-3",
  se: "bottom-0 right-0 h-3 w-3",
  sw: "bottom-0 left-0 h-3 w-3",
};

const FloatingResizeHandle = React.forwardRef<
  HTMLDivElement,
  { axis: ResizeHandleAxis } & React.HTMLAttributes<HTMLDivElement>
>(({ axis, ...props }, ref) => (
  <div
    ref={ref}
    className={twMerge(
      "absolute z-10 hover:bg-blue-300/40",
      HANDLE_CURSOR[axis],
      HANDLE_POSITION[axis],
    )}
    {...props}
  />
));
FloatingResizeHandle.displayName = "FloatingResizeHandle";

// ---------------------------------------------------------------------------
// Deterministic header coloring — all panels on the same port share a color,
// different ports get different colors.
// ---------------------------------------------------------------------------

const HEADER_COLORS = [
  "bg-blue-900 border-blue-800 hover:bg-blue-800",
  "bg-emerald-900 border-emerald-800 hover:bg-emerald-800",
  "bg-purple-900 border-purple-800 hover:bg-purple-800",
  "bg-amber-900 border-amber-800 hover:bg-amber-800",
  "bg-rose-900 border-rose-800 hover:bg-rose-800",
  "bg-cyan-900 border-cyan-800 hover:bg-cyan-800",
  "bg-indigo-900 border-indigo-800 hover:bg-indigo-800",
  "bg-teal-900 border-teal-800 hover:bg-teal-800",
] as const;

function getHeaderColor(): string {
  const port = typeof window !== "undefined" ? window.location.port : "";
  let hash = 0;
  for (const char of port) {
    hash = (hash * 31 + char.charCodeAt(0)) | 0;
  }
  return HEADER_COLORS[Math.abs(hash) % HEADER_COLORS.length]!;
}

// ---------------------------------------------------------------------------
// Minimized bar tiling registry
// ---------------------------------------------------------------------------

const MINIMIZED_BAR_WIDTH = 280;
const MINIMIZED_BAR_GAP = 8;
const MINIMIZED_BAR_LEFT_OFFSET = 16;

const minimizedPanels = new Set<string>();
const minimizedListeners = new Set<() => void>();
let minimizedSnapshot: readonly string[] = [];

function emitMinimizedChange() {
  minimizedSnapshot = [...minimizedPanels].sort();
  for (const listener of minimizedListeners) {
    listener();
  }
}

function subscribeMinimized(listener: () => void): () => void {
  minimizedListeners.add(listener);
  return () => minimizedListeners.delete(listener);
}

function getMinimizedSnapshot(): readonly string[] {
  return minimizedSnapshot;
}

function registerMinimized(key: string) {
  minimizedPanels.add(key);
  emitMinimizedChange();
}

function unregisterMinimized(key: string) {
  minimizedPanels.delete(key);
  emitMinimizedChange();
}

function useMinimizedIndex(storageKey: string, isMinimized: boolean): number {
  const sorted = React.useSyncExternalStore(
    subscribeMinimized,
    getMinimizedSnapshot,
    getMinimizedSnapshot,
  );

  React.useEffect(() => {
    if (isMinimized) {
      registerMinimized(storageKey);
      return () => unregisterMinimized(storageKey);
    }
  }, [storageKey, isMinimized]);

  return sorted.indexOf(storageKey);
}

// ---------------------------------------------------------------------------
// Geometry persistence
// ---------------------------------------------------------------------------

const panelGeometrySchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});

type PanelGeometry = z.infer<typeof panelGeometrySchema>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface FloatingPanelProps extends React.PropsWithChildren {
  title: string;
  storageKey: string;
  defaultSize: { width: number; height: number };
  defaultPosition?: "center" | "bottom-left";
  minSize?: { width: number; height: number };
  maxSize?: { width: number; height: number };
  onClose: () => void;
  onMinimize?: () => void;
  isMinimized?: boolean;
  headerExtra?: ReactNode;
}

function computeDefaultPosition(
  size: { width: number; height: number },
  position: "center" | "bottom-left",
) {
  if (typeof window === "undefined") {
    return { x: 100, y: 100 };
  }
  if (position === "bottom-left") {
    return {
      x: MINIMIZED_BAR_LEFT_OFFSET,
      y: window.innerHeight - size.height - MINIMIZED_BAR_LEFT_OFFSET,
    };
  }
  return {
    x: Math.round((window.innerWidth - size.width) / 2),
    y: Math.round((window.innerHeight - size.height) / 2),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FloatingPanel: React.FC<FloatingPanelProps> = ({
  title,
  storageKey,
  defaultSize,
  defaultPosition = "center",
  minSize = { width: 320, height: 200 },
  maxSize = { width: 2000, height: 1200 },
  onClose,
  onMinimize,
  isMinimized,
  children,
  headerExtra,
}) => {
  const defaultPos = computeDefaultPosition(defaultSize, defaultPosition);
  const [geometry, setGeometry] = useLocalStorageState(
    storageKey,
    { ...defaultPos, ...defaultSize } as PanelGeometry,
    { schema: panelGeometrySchema },
  );
  const nodeRef = React.useRef<HTMLDivElement>(null);
  const headerColor = React.useMemo(() => getHeaderColor(), []);
  const minimizedIndex = useMinimizedIndex(storageKey, Boolean(isMinimized));

  const { x, y, width, height } = geometry ?? {
    ...defaultPos,
    ...defaultSize,
  };

  if (isMinimized) {
    const left =
      minimizedIndex >= 0
        ? MINIMIZED_BAR_LEFT_OFFSET +
          minimizedIndex * (MINIMIZED_BAR_WIDTH + MINIMIZED_BAR_GAP)
        : MINIMIZED_BAR_LEFT_OFFSET;

    return (
      <div
        className={twMerge(
          "fixed bottom-0 z-[125] flex h-8 cursor-pointer items-center justify-between gap-3 rounded-t-md border border-b-0 px-3 shadow-lg",
          headerColor,
        )}
        style={{ left, width: MINIMIZED_BAR_WIDTH }}
        onClick={() => onMinimize?.()}
      >
        <span className="text-xs font-medium text-white">{title}</span>
        <div className="flex items-center gap-1.5">
          {headerExtra}
          <button
            type="button"
            className="text-slate-400 hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <X className="size-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <Draggable
      handle=".floating-panel-drag-handle"
      nodeRef={nodeRef as React.RefObject<HTMLElement>}
      position={{ x, y }}
      onStop={(_e, data) => {
        setGeometry((prev) => ({
          ...(prev ?? { ...defaultPos, ...defaultSize }),
          x: data.x,
          y: data.y,
        }));
      }}
      bounds="body"
    >
      <div
        ref={nodeRef}
        className="fixed z-[125] overflow-hidden rounded-xl"
        style={{ top: 0, left: 0, width, height }}
      >
        <Resizable
          width={width}
          height={height}
          minConstraints={[minSize.width, minSize.height]}
          maxConstraints={[maxSize.width, maxSize.height]}
          resizeHandles={RESIZE_HANDLES}
          handle={
            ((axis: ResizeHandleAxis, ref: React.Ref<HTMLDivElement>) => (
              <FloatingResizeHandle ref={ref} axis={axis} />
            )) as unknown as (resizeHandle: ResizeHandleAxis) => React.ReactNode
          }
          onResize={(_e, { size, handle }) => {
            setGeometry((prev) => {
              const g = prev ?? { ...defaultPos, ...defaultSize };
              const dw = size.width - g.width;
              const dh = size.height - g.height;
              return {
                width: size.width,
                height: size.height,
                x: handle.includes("w") ? g.x - dw : g.x,
                y: handle.includes("n") ? g.y - dh : g.y,
              };
            });
          }}
        >
          <div
            className="flex flex-col overflow-hidden rounded-xl border border-border bg-white shadow-lg"
            style={{ width, height }}
          >
            <div
              className={twMerge(
                "floating-panel-drag-handle flex h-8 shrink-0 cursor-grab items-center justify-between border-b px-3 active:cursor-grabbing",
                headerColor,
              )}
              onDoubleClick={() => onMinimize?.()}
            >
              <div className="flex items-center gap-1.5">
                <GripHorizontal className="size-3 text-slate-500" />
                <span className="select-none text-xs font-medium text-white">
                  {title}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {headerExtra}
                {onMinimize && (
                  <button
                    type="button"
                    className="rounded p-0.5 text-slate-400 hover:bg-white/10 hover:text-white"
                    onClick={onMinimize}
                  >
                    <Minus className="size-3" />
                  </button>
                )}
                <button
                  type="button"
                  className="rounded p-0.5 text-slate-400 hover:bg-white/10 hover:text-white"
                  onClick={onClose}
                >
                  <X className="size-3" />
                </button>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {children}
            </div>
          </div>
        </Resizable>
      </div>
    </Draggable>
  );
};

export { FloatingPanel };
